import * as RDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from './cn'
import { IconButton } from './IconButton'
import { usePrivateAttrs } from './privateArea'
import { t } from '@/lib/i18n'

export interface SheetProps {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: ReactNode
  /** Edge the panel slides in from. */
  side?: 'left' | 'right' | 'bottom'
  title: ReactNode
  description?: ReactNode
  hideTitle?: boolean
  hideClose?: boolean
  /** Panel width for left and right sheets (px, default 320, capped at 90vw). */
  width?: number
  footer?: ReactNode
  className?: string
  bodyClassName?: string
  /** Where focus goes on open (call event.preventDefault() and focus something); default: the first field or button. */
  onOpenAutoFocus?: (event: Event) => void
  children?: ReactNode
}

const sides = {
  left: 'inset-y-0 left-0 h-dvh border-r data-[state=open]:animate-sheet-left data-[state=closed]:animate-sheet-left-out',
  right: 'inset-y-0 right-0 h-dvh border-l data-[state=open]:animate-sheet-right data-[state=closed]:animate-sheet-right-out',
  bottom:
    'inset-x-0 bottom-0 max-h-[calc(100dvh-24px)] rounded-t-modal border-t pb-[env(safe-area-inset-bottom)] data-[state=open]:animate-sheet-up data-[state=closed]:animate-sheet-down',
}

/** Drawer panel that slides in from the left, right or bottom edge. */
export function Sheet({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  side = 'right',
  title,
  description,
  hideTitle,
  hideClose,
  width = 320,
  footer,
  className,
  bodyClassName,
  onOpenAutoFocus,
  children,
}: SheetProps) {
  const priv = usePrivateAttrs()
  return (
    <RDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger && <RDialog.Trigger asChild>{trigger}</RDialog.Trigger>}
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-50 bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
        <RDialog.Content
          className={cn('fixed z-50 flex flex-col border-line bg-surface text-text shadow-pop outline-none', sides[side], className)}
          style={side === 'bottom' ? undefined : { width: `min(${width}px, 90vw)` }}
          onOpenAutoFocus={onOpenAutoFocus}
          {...priv}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className={cn('shrink-0 px-5 pt-5 pr-14', hideTitle && 'sr-only')}>
            <RDialog.Title className="text-[18px] leading-7 font-bold tracking-[-0.01em]">{title}</RDialog.Title>
            {description && <RDialog.Description className="mt-1 text-body text-muted">{description}</RDialog.Description>}
          </div>
          <div className={cn('relative min-h-0 flex-1 overflow-y-auto px-5 py-4', bodyClassName)}>{children}</div>
          {footer && <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line px-5 py-4">{footer}</div>}
          {!hideClose && (
            <RDialog.Close asChild>
              <IconButton aria-label={t('Close')} icon={<X />} size={36} className="absolute top-3 right-3" />
            </RDialog.Close>
          )}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}
